<template>
  <div class="utility-query">
    <!-- Query Form View -->
    <div v-if="!showResults" class="query-form">
      <!-- AOI Section -->
      <div class="section section-aoi">
        <div class="section-label">
          <v-icon size="13" color="#888">radio_button_checked</v-icon>
          <span>Area of Interest (AOI)</span>
        </div>

        <div class="aoi-grid">
          <button v-for="aoi in aoiOptions" :key="aoi.id" class="aoi-btn"
            :class="{ active: selectedAoi === aoi.id, disabled: !allowedAoiIds.includes(aoi.id) }"
            :disabled="!allowedAoiIds.includes(aoi.id)"
            @click="selectAoi(aoi)">
            {{ aoi.label }}
          </button>
        </div>

        <!-- Selected AOI Chip -->
        <div v-if="selectedAoi" class="selected-chip">
          <v-icon size="12" color="#534ab7">location_on</v-icon>
          <span class="chip-text">{{ selectedAoiLabel }}</span>
          <v-icon size="13" color="#534ab7" class="chip-clear" @click="clearAoi">
            close
          </v-icon>
        </div>
      </div>

      <div class="divider" />

      <!-- Nearby Places Section -->
      <div class="section section-nearby">
        <div class="section-label">
          <v-icon size="13" color="#888">place</v-icon>
          <span>Nearby Places</span>
        </div>

        <div class="search-box">
          <v-icon size="15" color="#aaa">search</v-icon>
          <input v-model="nearbySearchQuery" placeholder="Search nearby places..." class="search-input" />
          <v-icon v-if="nearbySearchQuery" size="14" color="#aaa" class="clear-icon" @click="clearNearbySearch">
            close
          </v-icon>
        </div>
      </div>

      <div class="divider" />

      <!-- Utility Type Section -->
      <div class="section section-utility">
        <div class="section-label">
          <v-icon size="13" color="#888">electrical_services</v-icon>
          <span>Utility Point Type</span>
        </div>

        <div class="search-box">
          <v-icon size="15" color="#aaa">search</v-icon>
          <input v-model="searchQuery" placeholder="Search utility..." class="search-input" />
          <v-icon v-if="searchQuery" size="14" color="#aaa" class="clear-icon" @click="searchQuery = ''">
            close
          </v-icon>
        </div>

        <div class="utility-list">
          <div v-for="(categories, i) of categoryRows" :key="i" class="categories-row-container">
            <div v-for="type in categories" :key="type.id" class="category-item touchable-highlight-background"
              :class="{ selected: selectedTypes.includes(type.id) }" @click="toggleType(type.id)">
              <div class="category-icon"
                :style="{ backgroundImage: type.icon ? `url('https://utility.dev.aetosky.com/${type.icon}')` : 'none' }">
                <v-icon v-if="!type.icon" size="18" color="#534ab7">electrical_services</v-icon>
              </div>
              <div class="utility-info">
                <div class="utility-name">{{ type.name }}</div>
              </div>
            </div>
          </div>

          <div v-if="filteredUtilityTypes.length === 0" class="empty-state">
            <v-icon size="28" color="#ccc">search_off</v-icon>
            <span>No results found</span>
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="footer">
        <button class="btn-reset" @click="reset">
          <v-icon size="14">restart_alt</v-icon>
          Reset
        </button>
        <button class="btn-apply" :disabled="!hasSelection" @click="applyQuery">
          <v-icon size="14" color="white">manage_search</v-icon>
          Apply Query
        </button>
      </div>
    </div>

    <!-- Results View -->
    <div v-else class="results-view">
      <div class="results-header">
        <button class="btn-back" @click="backToQuery">
          <v-icon size="16" color="#534ab7">keyboard_backspace</v-icon>
          <span>Back</span>
        </button>
        <div class="results-title">
          <v-icon size="14" color="#888">list_alt</v-icon>
          <span>Query Results</span>
        </div>
      </div>

      <div class="results-summary">
        <span>Found <strong>{{ queryResults.utilities.length }}</strong> utility points in <strong>{{
          queryResults.aoiLabel }}</strong></span>
      </div>

      <div class="results-list">
        <div v-for="(point, index) in queryResults.utilities" :key="index"
          class="point-item-container touchable-highlight-background" @click="highlightPoint(point)">
          <div class="point-content-container">
            <div class="point-name-text">{{ point.feature_name }}</div>
            <div class="point-address-text">{{ point.location }}</div>
          </div>

          <div class="point-actions">
            <v-icon class="touchable-scale" @click.stop="zoomToUtility(point)">
              zoom_in
            </v-icon>
            <v-icon class="touchable-scale" @click.stop="
              highlightPoint(point);
            navigateToUtility(point);
            ">
              directions
            </v-icon>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import sdk from "@/api/sdk";
import { chunk } from "@/utils/Array";
import { navigate } from "@/utils/Navigation";

export default {
  name: "UtilityQuery",

  props: {
    aoiList: {
      type: Array,
      default: () => []
    },
    visibleLayerNames: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      selectedAoi: null,
      selectedTypes: [],
      searchQuery: "",
      utilityTypes: [],
      categories: [],
      queryResults: null,
      nearbySearchQuery: "",
      currentUtility: null,
      showResults: false,
      aoiOptions: [
        {
          id: "aoi1",
          label: "Aalo",
          lat: 28.1767,
          lon: 94.8,
          bounds: [
            [28.122503487046345, 94.65898442399222],
            [28.287634120618407, 94.82782943208286]
          ]
        },
        {
          id: "aoi2",
          label: "Bomdilla",
          lat: 27.2648,
          lon: 92.4247,
          bounds: [
            [27.24925590500527, 92.39877128841277],
            [27.279831479345773, 92.4316497195037]
          ]
        },
        {
          id: "aoi3",
          label: "Chowkham",
          lat: 27.9635,
          lon: 96.2316,
          bounds: [
            [27.786676128700492, 95.98083201518585],
            [27.82704930714385, 96.1210715432174]
          ]
        },
        {
          id: "aoi4",
          label: "Dirang",
          lat: 27.3513,
          lon: 92.262,
          bounds: [
            [27.336224862925498, 92.2231232508035],
            [27.380356070454926, 92.28720677920184]
          ]
        },
        {
          id: "aoi5",
          label: "Namsai",
          lat: 27.6667,
          lon: 95.8667,
          bounds: [
            [27.63133771778729, 95.83813255258394],
            [27.68174555178308, 95.9141348253342]
          ]
        },
        {
          id: "aoi6",
          label: "Longding",
          lat: 26.998,
          lon: 95.331,
          bounds: [
            [26.861359547282703, 95.29793360063825],
            [26.897042073561735, 95.33653072956689]
          ]
        },
        {
          id: "aoi7",
          label: "Mechuka",
          lat: 28.5972,
          lon: 94.1236,
          bounds: [
            [28.583797724186063, 94.0880409337437],
            [28.620213913004427, 94.15317705552853]
          ]
        },
        {
          id: "aoi8",
          label: "Pasighat",
          lat: 28.0667,
          lon: 95.3333,
          bounds: [
            [28.01116863181337, 95.26864037974818],
            [28.102083911388846, 95.36812831722905]
          ]
        },
        {
          id: "aoi9",
          label: "Roing",
          lat: 28.149,
          lon: 95.8245,
          bounds: [
            [28.099413646924184, 95.80461503468072],
            [28.164354608117744, 95.86379958318189]
          ]
        },
        {
          id: "aoi10",
          label: "Tawang",
          lat: 27.586,
          lon: 91.869,
          bounds: [
            [27.553736227045306, 91.85187171131048],
            [27.630256968834445, 91.8978542345853]
          ]
        }
      ]
    };
  },

  computed: {
    categoryRows() {
      return chunk(this.filteredUtilityTypes, 3);
    },

    filteredUtilityTypes() {
      if (!this.searchQuery.trim()) return this.utilityTypes;
      const q = this.searchQuery.toLowerCase();
      return this.utilityTypes.filter(t => t.name.toLowerCase().includes(q));
    },

    hasSelection() {
      return this.selectedAoi !== null || this.selectedTypes.length > 0 || this.nearbySearchQuery.trim() !== "";
    },

    selectedAoiBounds() {
      if (!this.selectedAoi) return null;
      const aoi = this.aoiOptions.find(a => a.id === this.selectedAoi);
      return aoi ? aoi.bounds : null;
    },

    selectedCount() {
      return this.selectedTypes.length;
    },

    selectedAoiLabel() {
      if (!this.selectedAoi) return "";
      const aoi = this.aoiOptions.find(a => a.id === this.selectedAoi);
      return aoi ? aoi.label : "";
    },

    allowedAoiIds() {
      if (this.visibleLayerNames.length === 0) return [];
      return this.aoiOptions
        .filter(aoi => this.visibleLayerNames.includes(aoi.label))
        .map(aoi => aoi.id);
    }
  },

  async created() {
    await this.fetchCategories();
  },

  methods: {
    async fetchCategories() {
      const res = await sdk.request({
        url: "/all-categories"
      });

      this.categories = res.data.data;

      this.utilityTypes = this.categories.map(c => ({
        id: c.id,
        name: c.name,
        icon: c.icon
      }));
    },

    selectAoi(aoi) {
      if (!this.allowedAoiIds.includes(aoi.id)) return;
      this.selectedAoi = aoi.id;
      this.$emit("pan-to-aoi", {
        bounds: aoi.bounds
      });
    },

    clearAoi() {
      this.selectedAoi = null;
    },

    clearResults() {
      this.queryResults = null;
      this.showResults = false;
      this.$emit("reset");
    },

    backToQuery() {
      this.showResults = false;
    },

    clearNearbySearch() {
      this.nearbySearchQuery = "";
    },

    toggleType(id) {
      const idx = this.selectedTypes.indexOf(id);
      if (idx === -1) this.selectedTypes.push(id);
      else this.selectedTypes.splice(idx, 1);
    },

    reset() {
      this.selectedAoi = null;
      this.selectedTypes = [];
      this.searchQuery = "";
      this.queryResults = null;
      this.nearbySearchQuery = "";
      this.showResults = false;
      this.$emit("reset");
    },

    applyQuery() {
      if (!this.hasSelection) return;
      if (this.selectedAoi && !this.allowedAoiIds.includes(this.selectedAoi)) return;
      this.fetchFilteredUtilities();
    },

    navigateToUtility(utility) {
      navigate({
        destination: {
          latitude: Number(utility.latitude),
          longitude: Number(utility.longitude)
        },
        params: [
          {
            key: "travelmode",
            value: "driving"
          },
          {
            key: "dir_action",
            value: "navigate"
          }
        ]
      });
    },

    zoomToUtility(utility) {
      this.$emit("pan-to-point", utility);
    },

    highlightPoint(point) {
      this.currentUtility = point;
      this.$emit("highlight-point", point);
    },

    async fetchFilteredUtilities() {
      try {
        const params = {};

        if (this.selectedTypes.length > 0) {
          params.category_id = this.selectedTypes.join(",");
        }

        if (this.selectedAoiBounds) {
          const b = this.selectedAoiBounds;
          params.bounds = `${b[0][0]},${b[0][1]},${b[1][0]},${b[1][1]}`;
        }

        if (this.nearbySearchQuery.trim()) {
          params.feature_name = this.nearbySearchQuery.trim();
        }

        const res = await sdk.request({
          url: "/utilities",
          params
        });

        const categoryDict = this.categories.reduce((dict, c) => {
          dict[c.id] = c;
          return dict;
        }, {});

        const utilities = res.data.data.map(u => {
          u.category = categoryDict[u.category_id];
          return u;
        });

        const typeCounts = {};
        utilities.forEach(u => {
          if (!typeCounts[u.category_id]) {
            typeCounts[u.category_id] = {
              id: u.category_id,
              name: u.category ? u.category.name : "Unknown",
              count: 0
            };
          }
          typeCounts[u.category_id].count++;
        });

        this.queryResults = {
          aoiLabel: this.selectedAoiLabel || this.nearbySearchQuery.trim() || "selected area",
          utilities,
          typeSummaries: Object.values(typeCounts)
        };

        this.showResults = true;

        this.$emit("apply-query", {
          selectedAoi: this.selectedAoi,
          selectedTypes: [...this.selectedTypes],
          featureName: this.nearbySearchQuery.trim() || null,
          utilities
        });
      } catch (error) {
        console.error("Failed to fetch utilities:", error);
      }
    }
  }
};
</script>

<style scoped>
.utility-query {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  background: white;
  overflow: hidden;
}

/* Section */
.section {
  padding: 14px 14px 0;
}

.section-aoi {
  padding: 14px 14px 12px;
  flex: 0 0 auto;
}

.section-nearby {
  padding: 14px 14px 12px;
  flex: 0 0 auto;
  position: relative;
}

.section-utility {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

/* AOI Grid */
.aoi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.aoi-btn {
  padding: 7px 4px;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: #666;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.aoi-btn:hover {
  border-color: #afa9ec;
  color: #534ab7;
  background: #f7f6fe;
}

.aoi-btn.active {
  background: #eeedfe;
  color: #534ab7;
  border-color: #afa9ec;
  font-weight: 600;
}

.aoi-btn.disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}

/* Selected AOI Chip */
.selected-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px 4px 10px;
  background: #eeedfe;
  border: 1px solid #d1cdf7;
  border-radius: 20px;
  margin-top: 8px;
}

.chip-text {
  font-size: 12px;
  font-weight: 500;
  color: #534ab7;
}

.chip-clear {
  cursor: pointer;
  opacity: 0.7;
}

.chip-clear:hover {
  opacity: 1;
}

/* Divider */
.divider {
  height: 1px;
  background: #f0f0f0;
}

/* Search Box */
.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f7f7f7;
  border: 1px solid #ebebeb;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 6px;
  transition: border-color 0.15s;
  flex: 0 0 auto;
}

.search-box:focus-within {
  border-color: #afa9ec;
  background: white;
}

.search-input {
  flex: 1;
  border: none;
  background: none;
  outline: none;
  font-size: 12.5px;
  color: #333;
}

.search-input::placeholder {
  color: #bbb;
}

.clear-icon {
  cursor: pointer;
  opacity: 0.6;
}

.clear-icon:hover {
  opacity: 1;
}

/* Nearby Dropdown */
.nearby-dropdown {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin-bottom: 6px;
  z-index: 10;
}

.nearby-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  cursor: pointer;
  font-size: 12.5px;
  color: #444;
  transition: background 0.12s;
}

.nearby-item:hover {
  background: #f7f6fe;
  color: #534ab7;
}

.nearby-item:not(:last-child) {
  border-bottom: 1px solid #f5f5f5;
}

.nearby-item-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Utility List */
.categories-row-container {
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.category-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: calc((100% - 16px) / 3);
  padding: 12px 8px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid #e0e0e0;
  background: white;
  transition: all 0.15s ease;
  outline: none;
  gap: 6px;
  position: relative;
}

.category-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.category-item:hover {
  border-color: #afa9ec;
  color: #534ab7;
  background: #f7f6fe;
}

.category-item.selected {
  background: #eeedfe;
  color: #534ab7;
  border-color: #afa9ec;
}

.utility-list {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding-bottom: 6px;
}

.utility-list::-webkit-scrollbar {
  width: 4px;
}

.utility-list::-webkit-scrollbar-track {
  background: transparent;
}

.utility-list::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 4px;
}

.utility-info {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.utility-name {
  font-size: 11px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 0;
  color: #ccc;
  font-size: 12px;
}

/* Query Form */
.query-form {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* Results View */
.results-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.results-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  background: white;
  font-size: 12px;
  font-weight: 500;
  color: #534ab7;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
}

.btn-back:hover {
  background: #f7f6fe;
  border-color: #afa9ec;
}

.results-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.results-summary {
  font-size: 11.5px;
  color: #555;
  padding: 12px 14px 8px;
  line-height: 1.4;
  flex-shrink: 0;
}

.results-list {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding-bottom: 6px;
}

.results-list::-webkit-scrollbar {
  width: 4px;
}

.results-list::-webkit-scrollbar-track {
  background: transparent;
}

.results-list::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 4px;
}

/* Point Item */
.point-item-container {
  display: flex;
  align-items: center;
  border-bottom: 0.0625em solid rgba(0, 0, 0, 0.1);
}

.point-content-container {
  flex: 1;
  min-width: 0;
  padding: 0.625em 0.9375em;
}

.point-name-text {
  font-size: 1em;
  font-weight: bold;
  color: var(--text-color);
  text-transform: capitalize;
}

.point-address-text {
  color: var(--secondary-text);
  text-transform: capitalize;
}

.point-actions {
  display: flex;
  gap: 0.5em;
  padding: 0.625em 0.9375em;
}

/* Footer */
.footer {
  flex: 0 0 auto;
  flex-shrink: 0;
  display: flex;
  gap: 8px;
  padding: 10px 14px 12px;
  border-top: 1px solid #f0f0f0;
  margin-top: 8px;
  align-items: center;
}

.btn-reset {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 0;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: white;
  font-size: 12.5px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
}

.btn-reset:hover {
  border-color: #ccc;
  color: #444;
  background: #fafafa;
}

.btn-apply {
  flex: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 0;
  border-radius: 8px;
  border: none;
  background: #534ab7;
  color: white;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
}

.btn-apply:hover:not(:disabled) {
  background: #3c3489;
}

.btn-apply:disabled {
  background: #c5c1ee;
  cursor: not-allowed;
}
</style>